import { Controller } from "@hotwired/stimulus"
import { uploadErrorMessage, renderUploadFailures, clearUploadFailures } from "../wps_pages/upload_errors"

// Escape user-controlled values before interpolating into innerHTML (matches the
// pattern used in tools/WYSIWYG.js). Prevents stored XSS via media titles/filenames.
const escapeHtml = (text) =>
  String(text == null ? "" : text).replace(/[&<>"']/g, (m) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;"
  }[m]))

// Only allow http(s) or root-relative URLs as image sources (blocks javascript: URIs).
const isSafeUrl = (url) => typeof url === "string" && /^(https?:|\/)/i.test(url)

export default class extends Controller {
  static targets = [
    "input",
    "preview",
    "modal",
    "grid",
    "search",
    "typeFilter",
    "loading",
    "emptyState",
    "selectedCount",
    "breadcrumb",
    "existingImage",
    "uploadInput",
    "uploadProgress",
    "uploadStatus",
    "uploadBar",
    "dragOverlay"
  ]

  static values = {
    mediaType: String,        // Filter by media type (image, video, document)
    multiple: { type: Boolean, default: false },  // Allow multiple selection
    fieldName: String,        // Name for the hidden input field
    selected: Array           // Currently selected media IDs
  }

  connect() {
    this.selectedValue = this.selectedValue || []
    this.page = 1
    this.loading = false
    this.currentFolder = null
    // Debug: Give each controller instance a unique ID
    this.controllerId = Math.random().toString(36).substr(2, 9)
    console.log(`[${this.controllerId}] Controller connected. Multiple:`, this.multipleValue)
  }

  open() {
    console.log(`[${this.controllerId}] Opening modal. Multiple:`, this.multipleValue)
    // Reset selections when opening modal
    this.selectedValue = []

    // Show modal
    this.modalTarget.showModal()

    // Load media
    this.loadMedia()
  }

  close() {
    this.modalTarget.close()
  }

  async loadMedia(append = false) {
    if (this.loading) return

    this.loading = true
    this.showLoading()

    const params = new URLSearchParams({
      page: this.page,
      per_page: 24
    })

    if (this.currentFolder) {
      params.append("folder", this.currentFolder)
    }

    if (this.mediaTypeValue) {
      params.append("media_type", this.mediaTypeValue)
    }

    if (this.hasSearchTarget && this.searchTarget.value) {
      params.append("search", this.searchTarget.value)
    }

    if (this.hasTypeFilterTarget && this.typeFilterTarget.value) {
      params.append("media_type", this.typeFilterTarget.value)
    }

    try {
      const response = await fetch(`/admin/media/browse?${params}`)
      const data = await response.json()

      if (append) {
        this.appendMedia(data.media)
      } else {
        // Render folders first, then media
        this.renderContent(data.folders || [], data.media || [])
        this.updateBreadcrumb(data.current_folder)
      }

      this.hasMore = data.has_more
      this.hideLoading()

      if ((data.folders?.length || 0) === 0 && (data.media?.length || 0) === 0 && !append) {
        this.showEmptyState()
      } else {
        this.hideEmptyState()
      }
    } catch (error) {
      console.error("Failed to load media:", error)
      this.hideLoading()
    } finally {
      this.loading = false
    }
  }

  renderContent(folders, mediaItems) {
    this.gridTarget.innerHTML = ""

    // Render folders first
    folders.forEach(folder => {
      this.gridTarget.appendChild(this.createFolderCard(folder))
    })

    // Then render media
    mediaItems.forEach(media => {
      this.gridTarget.appendChild(this.createMediaCard(media))
    })
  }

  renderMedia(mediaItems) {
    this.gridTarget.innerHTML = ""
    mediaItems.forEach(media => {
      this.gridTarget.appendChild(this.createMediaCard(media))
    })
  }

  appendMedia(mediaItems) {
    mediaItems.forEach(media => {
      this.gridTarget.appendChild(this.createMediaCard(media))
    })
  }

  createFolderCard(folder) {
    const card = document.createElement("div")
    card.className = "relative group cursor-pointer"
    card.dataset.folderPath = folder.path
    card.dataset.action = "click->media-picker#navigateToFolder"

    card.innerHTML = `
      <div class="aspect-square bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-colors flex items-center justify-center">
        <i class="fas fa-folder text-6xl text-blue-500"></i>
      </div>
      <div class="mt-2">
        <div class="text-sm font-medium text-gray-900 truncate" title="${escapeHtml(folder.name)}">
          <i class="fas fa-folder text-xs text-blue-500 mr-1"></i>
          ${escapeHtml(folder.name)}
        </div>
        <div class="text-xs text-gray-500">
          ${escapeHtml(folder.count)} item${folder.count !== 1 ? 's' : ''}
        </div>
      </div>
    `

    return card
  }

  createMediaCard(media) {
    const card = document.createElement("div")
    card.className = "relative group cursor-pointer"
    card.dataset.mediaId = media.id
    card.dataset.blobSignedId = media.blob_signed_id
    card.dataset.action = "click->media-picker#selectMedia"
    // Stable, full-resolution URL — used by the TinyMCE MediaLibraryPlugin override
    // path when inserting into editor content (never the thumbnail).
    const publicUrl = media.public_url || media.url || ""
    card.dataset.publicUrl = isSafeUrl(publicUrl) ? publicUrl : ""

    const isSelected = this.selectedValue.includes(media.id.toString())

    card.innerHTML = `
      <div class="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 ${isSelected ? 'border-green-500' : 'border-transparent'} hover:border-green-500 transition-colors">
        ${this.renderThumbnail(media)}
      </div>
      <div class="mt-2">
        <div class="text-sm font-medium text-gray-900 truncate" title="${escapeHtml(media.title)}">
          ${escapeHtml(media.title)}
        </div>
        <div class="text-xs text-gray-500">
          ${escapeHtml(media.file_size_human)}
        </div>
      </div>
      ${isSelected ? '<div class="absolute top-2 right-2 bg-green-500 text-white rounded-full p-1"><i class="fas fa-check text-xs"></i></div>' : ''}
    `

    return card
  }

  renderThumbnail(media) {
    if (media.media_type === "image" && isSafeUrl(media.thumbnail_url)) {
      return `<img src="${escapeHtml(media.thumbnail_url)}" alt="${escapeHtml(media.title)}" class="w-full h-full object-cover" loading="lazy">`
    } else if (media.media_type === "video") {
      return `<div class="flex items-center justify-center h-full bg-gray-900"><i class="fas fa-play-circle text-4xl text-white opacity-75"></i></div>`
    } else if (media.media_type === "audio") {
      return `<div class="flex items-center justify-center h-full bg-purple-100"><i class="fas fa-music text-3xl text-purple-600"></i></div>`
    } else if (media.media_type === "document") {
      return `<div class="flex items-center justify-center h-full bg-blue-100"><i class="fas fa-file-alt text-3xl text-blue-600"></i></div>`
    } else {
      return `<div class="flex items-center justify-center h-full bg-gray-200"><i class="fas fa-file text-3xl text-gray-400"></i></div>`
    }
  }

  selectMedia(event) {
    const card = event.currentTarget
    const mediaId = card.dataset.mediaId
    const blobSignedId = card.dataset.blobSignedId

    console.log(`[${this.controllerId}] selectMedia called. MediaId:`, mediaId, "Multiple:", this.multipleValue)

    if (this.multipleValue) {
      // Toggle selection for multiple mode
      const index = this.selectedValue.indexOf(mediaId)
      const borderDiv = card.querySelector(".aspect-square")

      if (index > -1) {
        // Deselect - use immutable array operation
        console.log(`[${this.controllerId}] Deselecting. Before:`, this.selectedValue)
        this.selectedValue = this.selectedValue.filter((id, i) => i !== index)
        console.log(`[${this.controllerId}] After deselect:`, this.selectedValue)
        borderDiv?.classList.remove("border-green-500")
        borderDiv?.classList.add("border-transparent")
        card.querySelector(".absolute.top-2")?.remove()
      } else {
        // Select - use immutable array operation
        console.log(`[${this.controllerId}] Selecting. Before:`, this.selectedValue)
        this.selectedValue = [...this.selectedValue, mediaId]
        console.log(`[${this.controllerId}] After select:`, this.selectedValue)
        borderDiv?.classList.remove("border-transparent")
        borderDiv?.classList.add("border-green-500")
        const checkmark = document.createElement("div")
        checkmark.className = "absolute top-2 right-2 bg-green-500 text-white rounded-full p-1"
        checkmark.innerHTML = '<i class="fas fa-check text-xs"></i>'
        card.appendChild(checkmark)
      }

      this.updateSelectedCount()
    } else {
      // Single selection mode - select and close
      this.selectedValue = [mediaId]
      this.updateInput(blobSignedId)
      this.updatePreview(card.querySelector("img")?.src, card.querySelector(".text-sm").textContent)
      this.close()
    }
  }

  confirmSelection() {
    console.log(`[${this.controllerId}] Confirm selection called. selectedValue:`, this.selectedValue, "multipleValue:", this.multipleValue)
    console.log(`[${this.controllerId}] gridTarget:`, this.gridTarget)

    const allCards = this.gridTarget.querySelectorAll("[data-media-id]")
    console.log(`[${this.controllerId}] All cards in grid:`, allCards.length)
    const allMediaIds = Array.from(allCards).map(c => c.dataset.mediaId)
    console.log(`[${this.controllerId}] All media IDs in grid:`, allMediaIds)

    if (this.selectedValue.length === 0) {
      alert("Please select at least one item")
      return
    }

    // For multiple selection, we need to collect all blob signed IDs and images
    const selectedCards = Array.from(this.gridTarget.querySelectorAll("[data-media-id]"))
      .filter(card => this.selectedValue.includes(card.dataset.mediaId))

    console.log(`[${this.controllerId}] Selected cards found:`, selectedCards.length)

    const blobSignedIds = selectedCards.map(card => card.dataset.blobSignedId)

    // Get thumbnail URLs for preview
    const thumbnails = selectedCards.map(card => {
      const img = card.querySelector("img")
      return img ? img.src : null
    }).filter(src => src !== null)

    this.updateInput(blobSignedIds)
    this.updateMultiplePreview(thumbnails)
    this.close()
  }

  updateInput(value) {
    if (this.hasInputTarget) {
      if (Array.isArray(value)) {
        // Multiple selection - create multiple hidden inputs
        this.inputTarget.innerHTML = ""
        value.forEach(blobSignedId => {
          const input = document.createElement("input")
          input.type = "hidden"
          input.name = this.fieldNameValue + "[]"
          input.value = blobSignedId
          this.inputTarget.appendChild(input)
        })
      } else {
        // Single selection - create a hidden input inside the div
        this.inputTarget.innerHTML = ""
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = this.fieldNameValue
        input.value = value
        this.inputTarget.appendChild(input)
      }
    }
  }

  updatePreview(imageUrl, title) {
    if (this.hasPreviewTarget) {
      // Hide existing image when showing a new preview
      if (this.hasExistingImageTarget) {
        this.existingImageTarget.classList.add("hidden")
      }

      if (imageUrl) {
        this.previewTarget.innerHTML = `
          <div class="relative group">
            <img src="${isSafeUrl(imageUrl) ? escapeHtml(imageUrl) : ''}" class="w-full aspect-square object-contain rounded-lg border-2 border-gray-200" />
            <button type="button"
                    data-action="click->media-picker#clearSelection"
                    class="absolute top-2 right-2 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
              <i class="fas fa-times mr-1"></i>
              Remove
            </button>
            <div class="mt-1 text-xs text-gray-600 truncate" title="${escapeHtml(title)}">${escapeHtml(title)}</div>
          </div>
        `
      } else {
        this.previewTarget.innerHTML = `
          <div class="p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-700">${escapeHtml(title)}</p>
              <button type="button"
                      data-action="click->media-picker#clearSelection"
                      class="text-xs text-red-600 hover:text-red-800 cursor-pointer">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        `
      }
    }
  }

  updateMultiplePreview(thumbnails) {
    if (this.hasPreviewTarget) {
      // Hide existing images when showing a new preview
      if (this.hasExistingImageTarget) {
        this.existingImageTarget.classList.add("hidden")
      }

      const imageGrid = thumbnails.map(src => `
        <img src="${isSafeUrl(src) ? escapeHtml(src) : ''}" class="w-full h-20 object-cover rounded-lg border-2 border-gray-200" />
      `).join('')

      this.previewTarget.innerHTML = `
        <div class="relative">
          <div class="grid grid-cols-2 gap-2">
            ${imageGrid}
          </div>
          <div class="mt-2 flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200">
            <span class="text-sm text-gray-700">${thumbnails.length} image${thumbnails.length !== 1 ? 's' : ''} selected</span>
            <button type="button"
                    data-action="click->media-picker#clearSelection"
                    class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 cursor-pointer">
              <i class="fas fa-times"></i>
              Clear all
            </button>
          </div>
        </div>
      `
    }
  }

  clearSelection() {
    if (!confirm("Are you sure you want to remove this image selection?")) {
      return
    }

    console.log(`[${this.controllerId}] Clearing selection`)
    this.selectedValue = []
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
      this.inputTarget.innerHTML = ""
    }
    if (this.hasPreviewTarget) {
      this.previewTarget.innerHTML = ""
    }
    // Show existing image again when clearing library selection
    if (this.hasExistingImageTarget) {
      this.existingImageTarget.classList.remove("hidden")
    }
  }

  removeExistingImage() {
    if (!confirm("Are you sure you want to remove this image? This will take effect when you save the form.")) {
      return
    }

    if (this.hasExistingImageTarget) {
      // Hide the existing image
      this.existingImageTarget.classList.add("hidden")

      // Add a hidden field to mark for deletion
      if (this.hasInputTarget) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = this.fieldNameValue.replace(/\[.*\]$/, "[remove_attachment]")
        input.value = "1"
        this.inputTarget.appendChild(input)
      }
    }
  }

  removeGalleryImage(event) {
    if (!confirm("Are you sure you want to remove this image? This will take effect when you save the form.")) {
      return
    }

    const button = event.currentTarget
    const blobId = button.dataset.blobId
    const imageContainer = button.closest(".relative")

    // Hide the image
    imageContainer.style.display = "none"

    // Add a hidden field to mark this attachment for deletion
    if (this.hasInputTarget) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.fieldNameValue.replace(/\[.*\]$/, "[remove_attachments][]")
      input.value = blobId
      this.inputTarget.appendChild(input)
    }
  }

  updateSelectedCount() {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = this.selectedValue.length
    }
  }

  search() {
    this.page = 1
    this.loadMedia()
  }

  filterByType() {
    this.page = 1
    this.loadMedia()
  }

  loadMore() {
    if (this.hasMore && !this.loading) {
      this.page++
      this.loadMedia(true)
    }
  }

  navigateToFolder(event) {
    const folderPath = event.currentTarget.dataset.folderPath
    this.currentFolder = folderPath
    this.page = 1
    this.loadMedia()
  }

  navigateToParent() {
    if (!this.currentFolder) return

    const parts = this.currentFolder.split("/")
    parts.pop()
    this.currentFolder = parts.length > 0 ? parts.join("/") : null
    this.page = 1
    this.loadMedia()
  }

  navigateToRoot() {
    this.currentFolder = null
    this.page = 1
    this.loadMedia()
  }

  updateBreadcrumb(currentFolder) {
    if (!this.hasBreadcrumbTarget) return

    const nav = this.breadcrumbTarget.querySelector('nav')

    // Always show home
    let html = `
      <button type="button"
              data-action="click->media-picker#navigateToRoot"
              class="flex items-center gap-1 text-gray-600 hover:text-green-600 transition-colors">
        <i class="fas fa-home"></i>
        <span>All Media</span>
      </button>
    `

    // Add folder parts
    if (currentFolder) {
      const parts = currentFolder.split("/")
      parts.forEach((part, index) => {
        const path = parts.slice(0, index + 1).join("/")
        const isLast = index === parts.length - 1

        html += `
          <i class="fas fa-chevron-right text-xs text-gray-400"></i>
        `

        if (isLast) {
          html += `
            <span class="font-medium text-gray-900">${escapeHtml(part)}</span>
          `
        } else {
          html += `
            <button type="button"
                    data-action="click->media-picker#navigateToBreadcrumb"
                    data-folder-path="${escapeHtml(path)}"
                    class="text-gray-600 hover:text-green-600 transition-colors">
              ${escapeHtml(part)}
            </button>
          `
        }
      })
    }

    nav.innerHTML = html
  }

  navigateToBreadcrumb(event) {
    const folderPath = event.currentTarget.dataset.folderPath
    this.currentFolder = folderPath
    this.page = 1
    this.loadMedia()
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  showEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.remove("hidden")
    }
  }

  hideEmptyState() {
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
  }

  // Upload functionality
  openFileUpload() {
    if (this.hasUploadInputTarget) {
      this.uploadInputTarget.click()
    }
  }

  async handleFileUpload(event) {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Upload the files
    await this.uploadFiles(files)

    // Reset file input
    event.target.value = ""
  }

  async uploadFile(file) {
    // Create FormData
    const formData = new FormData()
    formData.append("file", file)
    formData.append("title", file.name)

    // Add current folder if present
    if (this.currentFolder) {
      formData.append("folder", this.currentFolder)
    }

    // Upload to server
    const response = await fetch("/admin/media/uploads", {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfToken
      },
      body: formData
    })

    if (!response.ok) {
      throw new Error(await uploadErrorMessage(response))
    }

    return await response.json()
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // Drag and Drop handlers
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()

    // Show drag overlay
    if (this.hasDragOverlayTarget) {
      this.dragOverlayTarget.classList.remove("hidden")
    }

    // Set the dropEffect to show the correct cursor
    event.dataTransfer.dropEffect = "copy"
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()

    // Only hide overlay if we're leaving the modal container entirely
    // Check if the related target is outside the modal
    const modalContainer = this.modalTarget.querySelector('.bg-white')
    if (!modalContainer.contains(event.relatedTarget)) {
      if (this.hasDragOverlayTarget) {
        this.dragOverlayTarget.classList.add("hidden")
      }
    }
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()

    // Hide drag overlay
    if (this.hasDragOverlayTarget) {
      this.dragOverlayTarget.classList.add("hidden")
    }

    // Get dropped files
    const files = event.dataTransfer.files
    if (!files || files.length === 0) {
      return
    }

    // Upload the files using the same logic as the file input
    this.uploadFiles(files)
  }

  async uploadFiles(files) {
    // Show upload progress
    if (this.hasUploadProgressTarget) {
      this.uploadProgressTarget.classList.remove("hidden")
      clearUploadFailures(this.uploadProgressTarget)
    }

    const totalFiles = files.length
    let uploadedCount = 0
    const failures = []

    // Update status
    if (this.hasUploadStatusTarget) {
      this.uploadStatusTarget.textContent = `${uploadedCount} / ${totalFiles}`
    }

    // Upload each file
    for (const file of files) {
      try {
        await this.uploadFile(file)
        uploadedCount++

        // Update progress
        const progress = (uploadedCount / totalFiles) * 100
        if (this.hasUploadBarTarget) {
          this.uploadBarTarget.style.width = `${progress}%`
        }
        if (this.hasUploadStatusTarget) {
          this.uploadStatusTarget.textContent = `${uploadedCount} / ${totalFiles}`
        }
      } catch (error) {
        console.error("Upload failed for file:", file.name, error)
        failures.push({name: file.name, message: error.message})
      }
    }

    if (this.hasUploadProgressTarget) {
      renderUploadFailures(this.uploadProgressTarget, failures)
    }

    // Hide progress and reload media
    setTimeout(() => {
      // Leave the panel up when something failed — it is the only place the author
      // is told why the file is missing from the grid.
      if (this.hasUploadProgressTarget && failures.length === 0) {
        this.uploadProgressTarget.classList.add("hidden")
      }
      if (this.hasUploadBarTarget) {
        this.uploadBarTarget.style.width = "0%"
      }

      // Reload media to show newly uploaded files
      this.page = 1
      this.loadMedia()
    }, 1000)
  }
}
